import { useCallback, useEffect, useMemo, useState } from 'react'
import {
  Alert,
  Box,
  Chip,
  CircularProgress,
  Divider,
  Grid,
  Typography,
} from '@mui/material'
import CheckRoundedIcon from '@mui/icons-material/CheckRounded'
import { useDispatch } from 'react-redux'
import { useSnackbar } from 'notistack'
import OfoqThemeBtn from '../../../components/Buttons/OfoqThemeBtn.jsx'
import OfoqCheckBox from '../../../components/Forms/OfoqCheckBox.jsx'
import OfoqTextField from '../../../components/Forms/OfoqTextField.jsx'
import {
  ssoAddCompanyConfig,
  ssoEditCompanyConfig,
  ssoFetchCompanyMetadata,
  ssoGetCompanyConfig,
} from '../../../redux/features/companies/companiesThunk'
import UseTranslation from '../../../hooks/UseTranslation.jsx'
import SvgIconSize from '../../../components/Cards/helpers/SvgIconSize.jsx'
import { ReactComponent as LockIcon } from '../../../images/lock.svg'
import { ReactComponent as KeyIcon } from '../../../images/KeyIcon.svg'
import { ReactComponent as SingleCompanyIcon } from '../../../images/SingleCompany.svg'
import { ReactComponent as EditIcon } from '../../../images/Edit.svg'

const SSO_MODE_ADD = 'add'
const trimTrailingSlash = (value = '') => value.replace(/\/$/, '')
const buildSpEntityId = (baseUrl = '', domain = '') => {
  const trimmedBaseUrl = trimTrailingSlash(baseUrl)
  if (!trimmedBaseUrl) return ''
  const spEntityIdBase = `${trimmedBaseUrl}/saml`
  if (!domain) return spEntityIdBase
  return `${spEntityIdBase}/${domain}`
}
const getDefaultSsoValues = () => ({
  enabled: false,
  domain: '',
  entity_id: '',
  idp_metadata_url: '',
  idp_sso_url: '',
  idp_slo_url: '',
  idp_slo_binding: 'REDIRECT',
  idp_certificate: '',
  sp_base_url: '',
  sp_entity_id: '',
  employee_add_enabled: false,
})

const SsoConfigurationStep = ({
  ssoRef,
  companyId,
  companyName,
  mode,
  isEditFlow = false,
  canEnableSso = true,
  shouldFetchExistingSsoConfig = false,
  onSsoChoiceChange,
  onCanFinishSsoChange,
  onEditDetails,
}) => {
  const authEndpointUrl = process.env.REACT_APP_AUTH_ENDPOINT_URL || ''
  const defaultSpBaseUrl = trimTrailingSlash(authEndpointUrl)
  const dispatch = useDispatch()
  const { enqueueSnackbar } = useSnackbar()
  const [loading, setLoading] = useState(mode !== SSO_MODE_ADD)
  const [ssoChoice, setSsoChoice] = useState('disabled')
  const [ssoInitiallyEnabled, setSsoInitiallyEnabled] = useState(false)
  const [values, setValues] = useState(getDefaultSsoValues())
  const [metadataUrlInput, setMetadataUrlInput] = useState('')
  const isAddMode = mode === SSO_MODE_ADD && !isEditFlow
  const isEditMode = !isAddMode
  const isEnabled = ssoChoice === 'enabled'
  const hasMetadataUrl = Boolean((metadataUrlInput || values.idp_metadata_url || '').trim())
  const isSsoEnableBlocked = isAddMode && !canEnableSso
  const isSsoLockedInEdit = isEditMode && ssoInitiallyEnabled
  const canSelectEnabledChoice = (
    (isAddMode && !isSsoEnableBlocked) || (isEditMode && !isSsoLockedInEdit)
  )
  const canSelectDisabledChoice = isAddMode || (isEditMode && !isSsoLockedInEdit)
  const { t } = UseTranslation()

  const setField = (key) => (val) => {
    setValues((prev) => ({ ...prev, [key]: val || '' }))
  }
  const setDomainField = (val) => {
    const nextDomain = val || ''
    setValues((prev) => ({
      ...prev,
      domain: nextDomain,
      ...(isAddMode ? { sp_base_url: defaultSpBaseUrl } : {}),
      sp_entity_id: buildSpEntityId(prev.sp_base_url || defaultSpBaseUrl, nextDomain),
    }))
  }

  const requiredValid = useMemo(() => {
    const trim = (v) => String(v ?? '').trim()
    return Boolean(
      trim(values.domain) &&
      trim(values.entity_id) &&
      trim(values.idp_sso_url) &&
      trim(values.idp_slo_url) &&
      trim(values.idp_certificate) &&
      trim(values.sp_base_url) &&
      trim(values.sp_entity_id),
    )
  }, [
    values.domain,
    values.entity_id,
    values.idp_sso_url,
    values.idp_slo_url,
    values.idp_certificate,
    values.sp_base_url,
    values.sp_entity_id,
  ])

  const canFinishSsoStep = (
    !isEnabled ||
    (!loading && requiredValid && Boolean(companyId))
  )

  useEffect(() => {
    onCanFinishSsoChange?.(canFinishSsoStep)
  }, [canFinishSsoStep, onCanFinishSsoChange])

  useEffect(() => {
    const hasExistingSsoInEdit = !isAddMode && shouldFetchExistingSsoConfig
    setSsoChoice(hasExistingSsoInEdit ? 'enabled' : 'disabled')
    setValues({
      ...getDefaultSsoValues(),
      sp_base_url: isAddMode ? defaultSpBaseUrl : '',
      sp_entity_id: isAddMode ? buildSpEntityId(defaultSpBaseUrl, '') : '',
    })
    setSsoInitiallyEnabled(hasExistingSsoInEdit)
    setMetadataUrlInput('')
  }, [defaultSpBaseUrl, isAddMode, companyId, shouldFetchExistingSsoConfig])

  useEffect(() => {
    if (!isAddMode) return

    const effectiveBaseUrl = values.sp_base_url || defaultSpBaseUrl
    const nextSpEntityId = buildSpEntityId(effectiveBaseUrl, values.domain)

    if (
      values.sp_base_url === effectiveBaseUrl &&
      values.sp_entity_id === nextSpEntityId
    ) return

    setValues((prev) => ({
      ...prev,
      sp_base_url: prev.sp_base_url || defaultSpBaseUrl,
      sp_entity_id: buildSpEntityId(prev.sp_base_url || defaultSpBaseUrl, prev.domain),
    }))
  }, [defaultSpBaseUrl, isAddMode, values.domain, values.sp_base_url, values.sp_entity_id])

  useEffect(() => {
    if (!isSsoEnableBlocked) return
    if (ssoChoice !== 'disabled') setSsoChoice('disabled')
  }, [isSsoEnableBlocked, ssoChoice])

  useEffect(() => {
    if (!companyId || isAddMode) {
      setLoading(false)
      return
    }
    if (!shouldFetchExistingSsoConfig) {
      setLoading(false)
      return
    }
    setLoading(true)
    dispatch(ssoGetCompanyConfig({ id: companyId })).then((response) => {
      if (response?.meta?.requestStatus === 'fulfilled') {
        const payload = response.payload || {}
        setValues({
          ...getDefaultSsoValues(),
          ...payload,
          employee_add_enabled: Boolean(payload?.employee_add_enabled),
        })
        setMetadataUrlInput(payload?.idp_metadata_url || '')
        const payloadEnabled = Boolean(payload?.enabled)
        setSsoInitiallyEnabled(payloadEnabled)
        setSsoChoice(payloadEnabled ? 'enabled' : 'disabled')
      }
      setLoading(false)
    })
  }, [dispatch, companyId, isAddMode, shouldFetchExistingSsoConfig])

  const submitSsoStep = useCallback(async () => {
    if (!companyId) return false
    if (isAddMode && !isEnabled) return true
    if (isEnabled && !requiredValid) return false
    if (isEditMode && !isEnabled) {
      const disableResponse = await dispatch(ssoEditCompanyConfig({
        id: companyId,
        body: { enabled: false },
      }))
      if (disableResponse?.meta?.requestStatus !== 'fulfilled') return false
      enqueueSnackbar(t?.['SSO configuration updated'], { variant: 'success' })
      return true
    }

    let action = ssoEditCompanyConfig
    if (!isEditFlow && isAddMode) {
      action = ssoAddCompanyConfig
    }
    const body = {
      ...values,
      enabled: isEnabled,
    }
    const response = await dispatch(action({
      id: companyId,
      body,
    }))
    if (response?.meta?.requestStatus !== 'fulfilled') return false

    enqueueSnackbar(
      isAddMode
        ? (t?.['SSO configuration saved'])
        : (t?.['SSO configuration updated']),
      { variant: 'success' },
    )
    return true
  }, [
    companyId,
    dispatch,
    enqueueSnackbar,
    isAddMode,
    isEditFlow,
    isEditMode,
    isEnabled,
    requiredValid,
    t,
    values,
  ])

  useEffect(() => {
    if (ssoRef) ssoRef.current = { submitSsoStep }
    return () => {
      if (ssoRef) ssoRef.current = null
    }
  }, [ssoRef, submitSsoStep])

  useEffect(() => {
    onSsoChoiceChange?.(isEnabled)
  }, [isEnabled, onSsoChoiceChange])

  const handleAutofill = () => {
    const resolvedMetadataUrl = (
      metadataUrlInput || values.idp_metadata_url || ''
    ).trim()

    if (!resolvedMetadataUrl) return
    if (!companyId) return
    dispatch(ssoFetchCompanyMetadata({
      id: companyId,
      body: {
        metadata_url: resolvedMetadataUrl,
      },
    })).then((response) => {
      if (response?.meta?.requestStatus !== 'fulfilled') return

      const payload = response.payload || {}
      const resolvedDomain = payload?.domain || values.domain || ''
      const resolvedSpBaseUrl = isAddMode
        ? (defaultSpBaseUrl || payload?.sp_base_url || '')
        : (payload?.sp_base_url || values.sp_base_url || '')
      let resolvedSpEntityId = payload?.sp_entity_id || values.sp_entity_id || ''
      if (isAddMode && resolvedDomain && defaultSpBaseUrl) {
        resolvedSpEntityId = buildSpEntityId(defaultSpBaseUrl, resolvedDomain)
      }

      setValues({
        ...getDefaultSsoValues(),
        ...payload,
        idp_metadata_url: payload?.idp_metadata_url || resolvedMetadataUrl,
        sp_base_url: resolvedSpBaseUrl,
        sp_entity_id: resolvedSpEntityId,
        employee_add_enabled: Boolean(payload?.employee_add_enabled),
      })
      setMetadataUrlInput(payload?.idp_metadata_url || resolvedMetadataUrl)
      enqueueSnackbar(
        t?.['Metadata fetched and fields updated'],
        { variant: 'success' },
      )
    })
  }

  const ssoSubtitle = isSsoLockedInEdit
    ? t?.['SSO is active and cannot be disabled for this company']
    : t?.['Choose whether this company will use SAML 2.0 SSO via Microsoft Azure AD']

  return (
    <Box sx={styles.root}>
      <Box sx={styles.companyCard}>
        <Box sx={styles.sectionHeader}>
          <Box sx={styles.headerTitle}>
            <Box sx={styles.headerActions}>
              <Box sx={styles.headerIcon}>
                <SvgIconSize icon={<SingleCompanyIcon />} size="lg" customStyle={styles.companyIcon} />
              </Box>
              <Typography sx={styles.companyNameTitle}>
                {companyName || (t?.Company)}
              </Typography>
              <Chip
                size="small"
                label={mode === SSO_MODE_ADD ? (t?.Created) : (t?.Saved)}
                icon={isAddMode ? <CheckRoundedIcon sx={styles.headerSavedIcon} /> : undefined}
                sx={styles.headerSavedChip}
              />
            </Box>
            <Typography sx={styles.sectionSubtitle}>
              {t?.['Company record is ready. Configure SSO or skip to finish.']}
            </Typography>
          </Box>
            <OfoqThemeBtn
              onClick={onEditDetails}
              name={t?.['Edit details']}
              cancelStyle
              customStyle={styles.editBtn}
              startIcon={<SvgIconSize icon={<EditIcon />} size="xs" customStyle={styles.editBtnIcon} />}
            />
        </Box>
      </Box>

      <Box sx={styles.card}>
        <Box sx={styles.sectionHeader}>
          <Box sx={styles.sectionHeaderTitle}>
            <Typography sx={styles.sectionTitle}>
              {t?.['Single Sign-On (SSO)']}
            </Typography>
            <Typography sx={styles.sectionSubtitle}>
              {ssoSubtitle}
            </Typography>
          </Box>
          <Chip size="small" label="SAML 2.0" sx={styles.statusChip} />
        </Box>
        <Box sx={styles.sectionBody}>
          {loading ? (
            <Box sx={styles.loadingWrap}><CircularProgress /></Box>
          ) : (
            <>
              <Grid container spacing={2} sx={{ mb: 2 }}>
                <Grid item xs={12} md={6}>
                  <Box
                    role={canSelectEnabledChoice ? 'button' : 'region'}
                    tabIndex={canSelectEnabledChoice ? 0 : -1}
                    onClick={canSelectEnabledChoice ? () => setSsoChoice('enabled') : undefined}
                    onKeyDown={canSelectEnabledChoice ? (e) => e.key === 'Enter' && setSsoChoice('enabled') : undefined}
                    sx={[
                      styles.choiceCard,
                      isEnabled && styles.choiceCardSelected,
                      isSsoEnableBlocked && styles.choiceCardDisabled,
                    ]}
                  >
                      <Box
                        sx={[isAddMode && styles.choiceSelectDot,
                          isEnabled && styles.choiceSelectDotActive]}
                      />
                    <Box sx={styles.choiceCardHeader}>
                      <Box sx={styles.choiceIconBadge}>
                       <SvgIconSize icon={<LockIcon />} size="md" iconColor={styles.choiceIcon} />
                      </Box>
                      {isEditMode && isEnabled && (
                        <Chip size="small" label={t?.Active} sx={styles.activeChip} />
                      )}
                    </Box>
                    <Typography sx={styles.choiceTitle}>
                      {isEditMode && isSsoLockedInEdit ? (t?.['SSO - Enabled'] || 'SSO - Enabled') : t?.['Enable SSO']}
                    </Typography>
                    <Typography sx={styles.choiceBody}>
                      {isSsoEnableBlocked
                        ? (t?.['Requires Core module selection in General Details.'])
                        : t?.['Employees log in using Microsoft Azure AD credentials. Recommended for enterprise clients.']}
                    </Typography>
                    <Chip size="small" label={t?.['Azure AD / SAML 2.0']} sx={styles.choiceChip} />
                  </Box>
                </Grid>
                <Grid item xs={12} md={6}>
                  <Box
                    role={canSelectDisabledChoice ? 'button' : 'region'}
                    tabIndex={canSelectDisabledChoice ? 0 : -1}
                    onClick={canSelectDisabledChoice ? () => setSsoChoice('disabled') : undefined}
                    onKeyDown={canSelectDisabledChoice ? (e) => e.key === 'Enter' && setSsoChoice('disabled') : undefined}
                    sx={[
                      styles.choiceCard,
                      !isEnabled && styles.choiceCardSelected,
                      isSsoLockedInEdit && styles.choiceCardDisabled,
                    ]}
                  >
                    <Box
                      sx={[isAddMode && styles.choiceSelectDot,
                        !isEnabled && styles.choiceSelectDotActive]}
                    />
                    <Box sx={styles.choiceCardHeader}>
                      <Box sx={[styles.choiceIconBadge, styles.choiceIconBadgeNeutral]}>
                        <SvgIconSize icon={<KeyIcon />} size="md" iconColor={styles.choiceIcon} />
                      </Box>
                      {isEditMode && !isEnabled && (
                        <Chip size="small" label={t?.Active} sx={styles.activeChip} />
                      )}
                    </Box>
                    <Typography
                      sx={[styles.choiceTitle, isSsoLockedInEdit && styles.choiceTitleDisabled]}
                    >
                      {t?.['No SSO']}
                    </Typography>
                    <Typography
                      sx={[styles.choiceBody, isSsoLockedInEdit && styles.choiceBodyDisabled]}
                    >
                      {isSsoLockedInEdit
                        ? (t?.['Not available — SSO is permanently active for this company.'])
                        : (t?.['Users log in with a username and password managed within Orgarise. Can be enabled later.'])}
                    </Typography>
                    <Chip
                      size="small"
                      label={isSsoLockedInEdit ? (t?.Unavailable) : t?.['Standard login']}
                      sx={styles.choiceChipNeutral}
                    />
                  </Box>
                </Grid>
              </Grid>

              {!isEnabled && (
                <Box sx={styles.readyCard}>
                  <Box sx={styles.readyIconWrap}>
                    <CheckRoundedIcon sx={styles.readyIcon} />
                  </Box>
                  <Typography sx={styles.readyTitle}>
                    {t?.['Ready to create the company']}
                  </Typography>
                  <Typography sx={styles.readyBody}>
                    {t?.['Standard username/password authentication. SSO can be enabled later from company settings.']}
                  </Typography>
                </Box>
              )}

              {isEnabled && (
                <>
                  <Box sx={styles.autofillCard}>
                    <Box sx={styles.autofillHeader}>
                      <Typography sx={styles.autofillTitle}>
                        {t?.['Auto-fill from metadata URL']}
                      </Typography>
                      <Typography sx={styles.autofillHint}>
                        {t?.['Paste Azure AD Federation Metadata URL to populate all fields automatically']}
                      </Typography>
                    </Box>
                    <Box sx={styles.autofillActions}>
                      <Box sx={{ flex: 1, width: { xs: '100%', sm: 'max-content' } }}>
                        <OfoqTextField
                          name={t?.['Metadata URL']}
                          defaultValue={metadataUrlInput}
                          refState={setMetadataUrlInput}
                          type="url"
                          customPlaceHolder="https://login.microsoftonline.com/{tenant-id}/federationmetadata/2007-06/federationmetadata.xml"
                        />
                      </Box>
                      <OfoqThemeBtn
                        name={isEditMode ? t?.['Re-fetch & fill'] : t?.['Fetch & fill']}
                        onClick={() => handleAutofill()}
                        disabled={!hasMetadataUrl}
                        customStyle={styles.fetchBtn}
                      />
                    </Box>
                  </Box>

                  <Box sx={styles.manualDividerWrap}>
                    <Divider sx={styles.manualDivider} />
                    <Typography sx={styles.manualDividerText}>
                      {t?.['or fill in manually']}
                    </Typography>
                    <Divider sx={styles.manualDivider} />
                  </Box>
                  <Typography sx={styles.spTitle}>
                      {t?.['IDENTITY PROVIDER (IDP)']}
                    </Typography>
                  <Grid
                    container
                    spacing={2}
                  >
                    <Grid item xs={12} md={6}>
                      <OfoqTextField
                        name={t?.['Integration Type']}
                        defaultValue="SAML"
                        refState={() => null}
                        type="text"
                          disabled
                        customStyle={styles.serviceField}
                      />
                    </Grid>
                    <Grid item xs={12} md={6}>
                        <OfoqTextField
                          name={t?.['Domain Name']}
                          defaultValue={values.domain}
                          refState={setDomainField}
                          type="text"
                          required
                          customStyle={styles.inputField}
                          helperText={t?.['Email domain used for SSO user matching']}
                        />
                    </Grid>
                      <Grid item xs={12}>
                        <Typography sx={styles.checkboxTitle}>{t?.['Employee related']}</Typography>
                      <OfoqCheckBox
                        name={t?.['Link SSO users to existing employee records in OTMS']}
                        defaultOption={Boolean(values.employee_add_enabled)}
                        refState={(val) => setValues((prev) => ({
                          ...prev,
                          employee_add_enabled: val,
                        }))}
                        customStyle={styles.checkboxField}
                      />
                    </Grid>
                    <Grid item xs={12}>
                        <OfoqTextField
                          name={t?.['IdP metadata URL']}
                          defaultValue={values.idp_metadata_url}
                          refState={setField('idp_metadata_url')}
                          type="url"
                          customStyle={styles.idpField}
                        />
                    </Grid>
                    <Grid item xs={12}>
                        <OfoqTextField
                          name={t?.['IdP entity ID']}
                          defaultValue={values.entity_id}
                          refState={setField('entity_id')}
                          type="text"
                          required
                          customStyle={styles.idpField}
                        />
                    </Grid>
                    <Grid item xs={12} md={6}>
                      <OfoqTextField name={t?.['IdP SSO URL']} defaultValue={values.idp_sso_url} required refState={setField('idp_sso_url')} type="url" customStyle={styles.inputField}
                          helperText={t?.['Login endpoint']} />
                    </Grid>
                    <Grid item xs={12} md={6}>
                      <OfoqTextField name={t?.['IdP SLO URL']} defaultValue={values.idp_slo_url} required refState={setField('idp_slo_url')} type="url" customStyle={styles.inputField} helperText={t?.['Logout endpoint']} />
                    </Grid>
                    <Grid item xs={12}>
                      <OfoqTextField
                        name={t?.['IdP certificate']}
                        defaultValue={values.idp_certificate}
                        refState={setField('idp_certificate')}
                        type="text"
                        multiline
                        required
                        maxRows={4}
                        customStyle={styles.inputField}
                        helperText={t?.['Public signing certificate from Azure AD. Found under SAML Signing Certificate.']}
                      />
                    </Grid>
                  </Grid>

                  <Box sx={styles.spSection}>
                    <Typography sx={styles.spTitle}>
                      {t?.['SERVICE PROVIDER (SP)']}
                    </Typography>
                    <Alert severity="warning" sx={styles.spAlert}>
                      {t?.['Register these two URLs in the client\'s Azure AD enterprise application before testing SSO.']}
                    </Alert>
                    <Grid container spacing={2}>
                      <Grid item xs={12}>
                          <OfoqTextField name={t?.['SP base URL']} defaultValue={values.sp_base_url} refState={setField('sp_base_url')} type="url" required customStyle={styles.serviceField} />
                          <Typography sx={styles.spAlertText}>e.g. https://api-qa.unitedofoq.com/usermanagement</Typography>
                      </Grid>
                      <Grid item xs={12}>
                        <OfoqTextField name={t?.['SP entity ID']} defaultValue={values.sp_entity_id} refState={setField('sp_entity_id')} type="text" required customStyle={styles.serviceField} />
                        <Typography sx={styles.spAlertText}>e.g. https://api-qa.unitedofoq.com/usermanagement/saml/unitedofoq.com</Typography>
                      </Grid>
                    </Grid>
                  </Box>
                </>
              )}
            </>
          )}
        </Box>
      </Box>
    </Box>
  )
}

const styles = {
  root: {
    display: 'flex',
    flexDirection: 'column',
    gap: 2,
  },
  card: {
    backgroundColor: '#fff',
    borderRadius: '12px',
    boxShadow: '0 1px 2px rgba(27, 37, 89, 0.04)',
    marginBottom: { xs: '50px', sm: 'unset' },
  },
  companyCard: {
    backgroundColor: '#F0FBF7',
    border: '1px solid #e8eaed',
    borderRadius: '12px',
    boxShadow: '0 1px 2px rgba(27, 37, 89, 0.04)',
  },
  sectionHeader: {
    px: { xs: 2, sm: 3 },
    py: { xs: 2, sm: 2.5 },
    borderBottom: '1px solid #ebecef',
    display: 'flex',
    flexDirection: { xs: 'column', sm: 'row' },
    alignItems: { xs: 'flex-start', sm: 'center' },
    justifyContent: 'space-between',
    gap: 1.5,
    flexWrap: { xs: 'nowrap', sm: 'wrap' },
  },
  headerTitle: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'flex-start',
    justifyContent: 'space-between',
    gap: '5px',
    flexWrap: 'wrap',
  },
  sectionBody: {
    px: { xs: 2, sm: 3 },
    py: { xs: 2, sm: 2.5 },
  },
  sectionTitle: {
    fontSize: { xs: '1rem', sm: '1.0625rem' },
    fontWeight: 700,
    color: '#1b2559',
  },
  sectionSubtitle: {
    mt: 0.5,
    fontSize: '0.8125rem',
    color: '#92929d',
  },
  headerActions: {
    display: 'flex',
    alignItems: 'center',
    gap: 1,
  },
  headerIcon: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    width: 52,
    height: 52,
    borderRadius: '12px',
    backgroundColor: '#18a87a',
    border: '1px solid rgba(24, 168, 122, 0.5)',
    boxShadow: 'inset 0 1px 0 rgba(255, 255, 255, 0.25)',
  },
  companyIcon: {
    '& svg, & svg *': {
      stroke: '#ffffff !important',
      fill: 'none !important',
    },
  },
  companyNameTitle: {
    fontSize: { xs: '1.125rem', sm: '1.25rem' },
    fontWeight: 700,
    color: '#12715a',
    lineHeight: 1.25,
    letterSpacing: '-0.01em',
  },
  headerSavedChip: {
    height: 28,
    borderRadius: '999px',
    fontWeight: 700,
    fontSize: '0.75rem',
    color: '#0f7c5c',
    backgroundColor: 'rgba(41, 204, 139, 0.16)',
    border: '1px solid rgba(41, 204, 139, 0.5)',
    '& .MuiChip-icon': {
      marginLeft: 0.75,
      marginRight: '-2px',
    },
  },
  headerSavedIcon: {
    fontSize: 12,
    color: '#0f7c5c',
  },
  statusChip: {
    height: 26,
    fontWeight: 600,
    fontSize: '0.6875rem',
    color: '#29CC8B',
    backgroundColor: 'rgba(41, 204, 139, 0.12)',
    border: '1px solid rgba(41, 204, 139, 0.45)',
  },
  editBtn: {
    maxWidth: { xs: '100%', sm: '150px' },
    textTransform: 'capitalize',
    minHeight: '40px',
    borderRadius: '10px',
    ' &:hover': {
      color: '#ffffff',
    },
  },
  editBtnIcon: {
    color: 'inherit',
    '& svg': {
      width: 14,
      height: 14,
    },
    '& svg, & svg *': {
      stroke: 'currentColor',
      fill: 'none',
    },
  },
  choiceCard: {
    border: '1px solid #e2e8ee',
    borderRadius: '14px',
    p: 2,
    minHeight: 140,
    height: 180,
    backgroundColor: '#fff',
    cursor: 'pointer',
    position: 'relative',
  },
  choiceSelectDot: {
    position: 'absolute',
    top: 12,
    right: 12,
    width: 16,
    height: 16,
    borderRadius: '50%',
    border: '1.5px solid #98a2b3',
    backgroundColor: '#fff',
    boxSizing: 'border-box',
  },
  choiceSelectDotActive: {
    borderColor: '#29CC8B',
    backgroundColor: '#29CC8B',
    boxShadow: 'inset 0 0 0 2px #ffffff',
  },
  choiceCardHeader: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    mb: 1,
  },
  choiceIconBadge: {
    width: 44,
    height: 44,
    borderRadius: '12px',
    backgroundColor: 'rgba(41, 204, 139, 0.15)',
    border: '1px solid rgba(41, 204, 139, 0.35)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  },
  choiceIconBadgeNeutral: {
    backgroundColor: '#f2f4f7',
    border: '1px solid #e4e7ec',
  },
  choiceIcon: {
    color: '#29cc8b',
    fontSize: 20,
  },
  choiceCardSelected: {
    border: '1px solid #29CC8B',
    backgroundColor: 'rgba(41, 204, 139, 0.08)',
  },
  choiceCardDisabled: {
    backgroundColor: '#fafbfc',
    borderColor: '#e7eaef',
    cursor: 'not-allowed',
    opacity: 0.85,
  },
  choiceTitle: {
    fontWeight: 700,
    color: '#1b2559',
    fontSize: '1rem',
  },
  choiceTitleDisabled: {
    color: '#9aa1ab',
  },
  choiceBody: {
    mt: 1,
    mb: 1.5,
    fontSize: '0.875rem',
    color: '#696974',
    lineHeight: 1.45,
  },
  choiceBodyDisabled: {
    color: '#b0b6bf',
  },
  activeChip: {
    color: '#c24141',
    backgroundColor: '#fdecec',
    border: '1px solid #f7c2c2',
    fontWeight: 600,
  },
  choiceChip: {
    color: '#1a8f5f',
    backgroundColor: 'rgba(41, 204, 139, 0.12)',
    border: '1px solid rgba(41, 204, 139, 0.35)',
  },
  choiceChipNeutral: {
    color: '#667085',
    backgroundColor: '#f2f4f7',
    border: '1px solid #eaecf0',
  },
  readyCard: {
    border: '1px solid #e2e8ee',
    borderRadius: '14px',
    p: { xs: 3, sm: 4 },
    backgroundColor: '#fafbfc',
    textAlign: 'center',
  },
  readyIconWrap: {
    width: 52,
    height: 52,
    borderRadius: '50%',
    border: '1px solid rgba(41, 204, 139, 0.35)',
    backgroundColor: 'rgba(41, 204, 139, 0.12)',
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    mb: 1.25,
  },
  readyIcon: {
    color: '#19935f',
    fontSize: 24,
  },
  readyTitle: {
    fontWeight: 700,
    fontSize: { xs: '1.1rem', sm: '1.4rem' },
    color: '#101828',
  },
  readyBody: {
    mt: 1,
    maxWidth: 520,
    mx: 'auto',
    fontSize: '0.875rem',
    color: '#667085',
    lineHeight: 1.6,
  },
  autofillCard: {
    border: '1px solid rgba(41, 204, 139, 0.35)',
    backgroundColor: 'rgba(41, 204, 139, 0.1)',
    borderRadius: '12px',
    p: 2,
    mb: 2,
  },
  autofillHeader: {
    mb: 1,
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'flex-start',
    justifyContent: 'space-between',
    gap: '5px',
    flexWrap: 'wrap',
  },
  autofillTitle: {
    fontWeight: 700,
    color: '#1a8f5f',
    fontSize: '0.95rem',
  },
  autofillHint: {
    mt: 0.25,
    color: '#228b67',
    fontSize: '0.8125rem',
  },
  autofillActions: {
    display: 'flex',
    gap: 1,
    alignItems: 'flex-end',
    flexDirection: { xs: 'column', sm: 'row' },
  },
  fetchBtn: {
    textTransform: 'none',
    minHeight: '40px',
    mt: '8px',
    borderRadius: '10px',
    maxWidth: { xs: '100%', sm: 'fit-content !important' },
    width: { xs: '100%', sm: 'fit-content !important' },
    fontFamily: 'Avenir-bold',
    fontSize: '0.875rem',
    backgroundColor: '#29CC8B',
    marginInline: { xs: '0px', sm: '10px' },
    color: '#fff',
    '&:hover': {
      backgroundColor: '#24b87d',
    },
    '&:disabled': {
      backgroundColor: '#29CC8B',
      opacity: 0.5,
      color: '#fff',
      cursor: 'not-allowed',
    },
  },
  manualDividerWrap: {
    display: 'flex',
    alignItems: 'center',
    gap: 1.5,
    mb: 2,
  },
  manualDivider: {
    flex: 1,
  },
  manualDividerText: {
    color: '#98a2b3',
    fontSize: '0.8125rem',
  },
  spSection: {
    mt: 2.5,
    pt: 2,
    borderTop: '1px solid #ebecef',
  },
  spTitle: {
    fontWeight: 700,
    fontSize: '0.8125rem',
    color: '#667085',
    letterSpacing: '0.06em',
    textTransform: 'uppercase',
    mb: 1.25,
  },
  spAlert: {
    mb: 1.5,
    borderRadius: '10px',
    backgroundColor: 'warning.light',
    border: '1px solid rgba(255, 197, 66, 0.35)',
    color: 'rgba(184, 131, 9, 0.72)',
    padding: '10px',
    fontSize: '14px',
    wordBreak: 'break-word',
    textAlign: 'left',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  },
  loadingWrap: {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    py: 8,
  },
  sectionHeaderTitle: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'flex-start',
    justifyContent: 'space-between',
    gap: '5px',
    flexWrap: 'wrap',
  },
  inputField: {
    '& .MuiFormLabel-root': {
      fontSize: '14px',
    },
    '& .MuiFormHelperText-root': {
      display: 'flex !important',
      color: '#696974',
    },
  },
  serviceField: {
    '& .MuiFormLabel-root': {
      fontSize: '14px',
    },
  },
  idpField: {
    '& .MuiFormLabel-root': {
      fontSize: '14px',
    },
  },
  checkboxField: {
    mx: '2px',
    height: '50px',
    width: '100%',
    borderRadius: '10px',
    border: '1px solid #e8eaed',
    backgroundColor: '#fafbfc',
    color: '#667085',
    '& .MuiFormControlLabel-root': {
      fontSize: '14px',
    },
  },
  checkboxTitle: {
    fontSize: '14px',
    mb: 1,
  },
  spAlertText: {
    my: 2,
    borderRadius: '10px',
    backgroundColor: 'primary.light',
    border: '1px solid rgba(41, 204, 139, 0.35)',
    color: 'rgba(8, 167, 103, 0.72)',
    padding: '10px',
    fontSize: '14px',
    wordBreak: 'break-all',
  },
}

export default SsoConfigurationStep
